import autoTable from "jspdf-autotable";
import http from "Utils/api2";
import { formattedDateServer } from "Utils/dateFunc";

// PDF-safe price formatter — jsPDF default font (Helvetica) cannot render ₹,
// so we use 'Rs.' prefix with ASCII characters only.
const formatPricePDF = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return "Rs. " + num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const { default: jsPDF } = require("jspdf");

// Function to fetch data from a specific page
const fetchData = async (url, page) => {
  try {
    // Safely append page param — use & if URL already has ?, else use ?
    const separator = url.includes("?") ? "&" : "?";
    const res = await http.get(`${url}${separator}page=${page}`);
    return res.data?.data || [];
  } catch (error) {
    return [];
  }
};

export const generatePDFWithAllOrderData = async ({ url, totalPages, loading, ecom }) => {
  loading(true);

  try {
    const pagesToFetch = totalPages || 1;
    const pagePromises = [];

    for (let page = 1; page <= pagesToFetch; page++) {
      pagePromises.push(fetchData(url, page));
    }

    const isData = await Promise.all(pagePromises);

    const combinedData = isData.filter(Boolean).flat();

    

    pdfGenerator(combinedData, ecom);
  } catch (error) {
    
  } finally {
    loading(false);
  }
};

// PDF generator with proper alignment for both Service & Ecommerce orders
const pdfGenerator = (data, ecom) => {
  const doc = new jsPDF({ orientation: "landscape" });

  // ── Page Title ──────────────────────────────────────────────────
  const title = ecom ? "E-Commerce Orders Report" : "Service Orders Report";
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(title, doc.internal.pageSize.getWidth() / 2, 14, { align: "center" });
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(
    `Generated: ${formattedDateServer(new Date())}`,
    doc.internal.pageSize.getWidth() / 2,
    20,
    { align: "center" }
  );

  // ── Data Rows ────────────────────────────────────────────────────
  const newData = data?.map((ele, i) => {
    const userName =
      ele?.customerId?.fullName ||
      ele?.userId?.fullName ||
      (ele?.address?.firstName
        ? `${ele.address.firstName} ${ele.address.lastName}`
        : "N/A");

    const orderId = ele?._id || "-";

    const status = ele?.bookingStatus || ele?.status || "-";

    const paymentMethod = ele?.paymentMethod || "-";

    const rawPrice = ele?.finalPayableAmount ?? ele?.orderTotal ?? null;
    const totalPrice = rawPrice !== null ? formatPricePDF(rawPrice) : "-";

    // Ecom orders use createdAt; service orders use serviceDateTime
    const dateValue = ecom
      ? ele?.createdAt || ele?.serviceDateTime
      : ele?.serviceDateTime || ele?.createdAt;

    const placeOrder = dateValue ? formattedDateServer(new Date(dateValue)) : "-";

    return [i + 1, userName, orderId, status, paymentMethod, totalPrice, placeOrder];
  });

  // ── Table ─────────────────────────────────────────────────────────
  autoTable(doc, {
    startY: 26,
    head: [
      [
        "S.No",
        "User Name",
        "Order ID",
        "Order Status",
        "Payment Method",
        "Total Price",
        ecom ? "Order Date" : "Working Date",
      ],
    ],
    body: newData,

    // ── Column widths & alignment ──────────────────────────────────
    columnStyles: {
      0: { cellWidth: 12,  halign: "center" }, // S.No
      1: { cellWidth: 55,  halign: "left"   }, // User Name
      2: { cellWidth: 55,  halign: "left"   }, // Order ID
      3: { cellWidth: 32,  halign: "center" }, // Status
      4: { cellWidth: 28,  halign: "center" }, // Payment
      5: { cellWidth: 28,  halign: "right"  }, // Total Price
      6: { cellWidth: 38,  halign: "center" }, // Date
    },

    // ── Head styles ────────────────────────────────────────────────
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontStyle: "bold",
      halign: "center",
      fontSize: 9,
    },

    // ── Body styles ────────────────────────────────────────────────
    bodyStyles: {
      fontSize: 8,
      cellPadding: 3,
      overflow: "linebreak",
      valign: "middle",
    },

    // ── Alternate row color ────────────────────────────────────────
    alternateRowStyles: {
      fillColor: [235, 245, 255],
    },

    // ── Table border ───────────────────────────────────────────────
    tableLineColor: [189, 195, 199],
    tableLineWidth: 0.2,

    margin: { top: 26, left: 10, right: 10 },

    // ── Page footer ─────────────────────────────────────────────────
    didDrawPage: (hookData) => {
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Page ${hookData.pageNumber} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 5,
        { align: "center" }
      );
    },
  });

  const fileName = ecom ? "E-Commerce Orders Report.pdf" : "Service Orders Report.pdf";
  doc.save(fileName);
};
